import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import {
    Box,
    Collapse,
    styled,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel
} from '@mui/material';
import { SxProps, Theme, useTheme } from '@mui/material';
import { Icon, Spinner } from '../../atoms';
import { useTranslation } from 'react-i18next';

const ExpandButton = styled('button', {
    shouldForwardProp: (prop) => prop !== 'isExpanded'
})<{ isExpanded?: boolean }>(({ isExpanded, theme }) => ({
    border: 'none',
    backgroundColor: theme.palette.semantic.bg.default,
    cursor: 'pointer',
    padding: 0,
    margin: 0,
    width: '52px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    ...(isExpanded && {
        svg: {
            transform: 'rotate(90deg)'
        }
    })
}));
const StyledTable = styled(Table, {
    shouldForwardProp: (prop) => prop !== 'advisorConsoleRevampFF' && prop !== 'ftds' && prop !== 'embedded'
})<{ advisorConsoleRevampFF?: boolean; ftds?: boolean; embedded?: boolean | 'all' }>(({ theme, advisorConsoleRevampFF, ftds, embedded }) => {
    const isEmbedded = !!embedded;
    const isEmbeddedAll = embedded === 'all';
    return {
    tableLayout: 'fixed',
    width: '100%',
    '&.MuiTable-root': {
        borderCollapse: 'separate',
    },
    '& .MuiTableCell-root .MuiTableCell-body': {
        backgroundColor: 'transparent',
        borderLeft: 'none',
        borderRight: 'none',
        borderTop: 'none',
        borderBottom: `1px solid ${theme.palette.divider}`,
        fontWeight: 500,
        lineHeight: '20px',
        fontSize: '16px'
    },
    '& .MuiTableHead-root .MuiTableCell-head': {
        fontWeight: ftds || advisorConsoleRevampFF ? 700 : 600,
        lineHeight: ftds ? '19px' : '20px',
        fontSize: ftds || advisorConsoleRevampFF ? '14px' : '16px',
        color: ftds ? theme.palette.neutral[950] : theme.palette.semantic.text.default,
        borderBottom: ftds ? 'none' : `1px solid ${theme.palette.semantic.bd.strong}`,
        borderTop: ftds ? 'none' : `1px solid ${theme.palette.semantic.bd.strong}`,
        height: '51px',
        border: `1px solid ${theme.palette.semantic.bd.default}`,
        '&:first-child': {
            borderTopLeftRadius: isEmbedded ? '0' : '10px'
        },
        '&:last-child': {
            borderTopRightRadius: isEmbedded ? '0' : '10px'
        }

    },
    '& .MuiTableBody-root': {
        '& tr:last-child td:first-child': {
            borderBottomLeftRadius: isEmbedded ? '0' : '10px'
        },
        '& tr:last-child td:last-child': {
            borderBottomRightRadius: isEmbedded ? '0' : '10px'
        }
    },
    '& .MuiTableCell-root': {
        border: `1px solid ${theme.palette.semantic.bd.default}`,
        ...(isEmbedded && {
            '&:first-child': {
                borderLeft: 'none'
            },
            '&:last-child': {
                borderRight: 'none'
            }
        })
    },
    ...(isEmbedded && {
        '& .MuiTableHead-root tr:first-of-type .MuiTableCell-root': {
            borderTop: 'none'
        }
    }),
    ...(isEmbeddedAll && {
        '& .MuiTableBody-root tr:last-child .MuiTableCell-root': {
            borderBottom: 'none'
        }
    })
    // '& .MuiTableRow-root:last-of-type .MuiTableCell-root': {
    //     borderBottom: 'none'
    // }
    };
});

const StyledTableHeaderRow = styled(TableRow, {
    shouldForwardProp: (prop) => prop !== '$rowHeight'
})<{ $rowHeight?: string }>(({ theme, $rowHeight }) => ({
    height: $rowHeight || '48px',
    alignItems: 'center',
    alignSelf: 'stretch',
    backgroundColor: theme.palette?.semantic?.bg?.raised
}));

const StyledTableRow = styled(TableRow, {
    shouldForwardProp: (prop) => prop !== '$rowHeight'
})<{ $rowHeight?: string }>(({ $rowHeight }) => ({
    height: $rowHeight || '48px'
}));

const NoDataContainer = styled('div')(() => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '300px',
    color: '#797979',
    fontSize: '1rem'
}));

type ColumnAlign = 'left' | 'center' | 'right' | 'inherit' | 'justify';

type ColumnDef = {
    label: string;
    // Optional node (e.g. an info tooltip) rendered next to the header label, outside the sort control.
    labelInfo?: React.ReactNode;
    key: string;
    align: ColumnAlign;
    width: string;
    sortable?: boolean;
    headerSx?: SxProps<Theme>;
    cellSx?: SxProps<Theme>;
    // eslint-disable-next-line no-unused-vars
    sortEquation?: (a: any, b: any, order: 'asc' | 'desc') => number;
    // eslint-disable-next-line no-unused-vars
    render?: (value: any, row: any) => React.ReactNode;
    headerRender?: () => React.ReactNode;
};

type SubColumnDef = {
    key: string;
    align: ColumnAlign;
    width: string;
    // eslint-disable-next-line no-unused-vars
    render?: (value: any, row: any) => React.ReactNode;
};

type GroupHeaderDef = {
    label: React.ReactNode;
    colSpan: number;
    align?: ColumnAlign;
    rowSpan?: number;
    width?: string;
    sx?: SxProps<Theme>;
};

type tableProps = {
    headers: ColumnDef[];
    dataSource: any[];
    // eslint-disable-next-line no-unused-vars
    conditionalRowFormat?: (row?: any) => SxProps<Theme> | undefined;
    subItemHeaders?: SubColumnDef[];
    // eslint-disable-next-line no-unused-vars
    customRowComp?: (
        // eslint-disable-next-line no-unused-vars
        headers: ColumnDef[],
        // eslint-disable-next-line no-unused-vars
        row: any,
        // eslint-disable-next-line no-unused-vars
        toggleRow: (key: string | number) => void,
        // eslint-disable-next-line no-unused-vars
        isExpanded: boolean,
        // eslint-disable-next-line no-unused-vars
        rowKey: string | number
    ) => React.ReactNode;
    // eslint-disable-next-line no-unused-vars
    customSubRowComp?: (columns: SubColumnDef[], row: any) => React.ReactNode;
    rowHeight?: string;
    showExpandButton?: boolean;
    loading?: boolean;
    groupHeaders?: GroupHeaderDef[];
    stickyFirstColumn?: boolean;
    horizontalScroll?: boolean;
    autoWidth?: boolean;
    bordered?: boolean;
    variant?: 'default' | 'ftds';
    borderColor?: string;
    bodyRowHeight?: string;
    maxHeight?: string;
    embedded?: boolean | 'all';
    trailingMergedCell?: {
        fromColumnKey: string;
        content: React.ReactNode;
        sx?: SxProps<Theme>;
    };
};
export const TableWithCustomRender: React.FC<tableProps> = ({ 
    headers,
    dataSource,
    conditionalRowFormat,
    subItemHeaders,
    customRowComp,
    customSubRowComp,
    rowHeight,
    showExpandButton = true,
    loading = false,
    groupHeaders,
    stickyFirstColumn = false,
    horizontalScroll = false,
    autoWidth = false,
    bordered = false,
    variant = 'default',
    borderColor,
    bodyRowHeight,
    maxHeight,
    embedded = false,
    trailingMergedCell
}) => {
    const theme = useTheme();
    const [expandedRows, setExpandedRows] = useState<Set<string | number>>(new Set());
    const [orderBy, setOrderBy] = useState<string | null>(null);
    const [order, setOrder] = useState<'asc' | 'desc'>('asc');
    const groupHeaderRowRef = useRef<HTMLTableRowElement>(null);
    const [groupHeaderHeight, setGroupHeaderHeight] = useState<number | null>(null);
    const { t } = useTranslation();

    useLayoutEffect(() => {
        const el = groupHeaderRowRef.current;
        if (!el) {
            setGroupHeaderHeight(null);
            return;
        }
        const measure = () => setGroupHeaderHeight(el.getBoundingClientRect().height);
        measure();
        if (typeof ResizeObserver === 'undefined') return;
        const observer = new ResizeObserver(measure);
        observer.observe(el);
        return () => observer.disconnect();
    }, [groupHeaders, rowHeight]);

    const handleSort = (column: ColumnDef) => {
        const isAsc = orderBy === column.key && order === 'asc';
        const newOrder: 'asc' | 'desc' = isAsc ? 'desc' : 'asc';
        setOrderBy(column.key);
        setOrder(newOrder);
    };

    const sortedData = (() => {
        if (!orderBy) return dataSource;
        const activeHeader = headers.find((h) => h.key === orderBy);
        if (activeHeader?.sortEquation) {
            return [...dataSource].sort((a, b) =>
                activeHeader && activeHeader?.sortEquation ? activeHeader.sortEquation(a, b, order) : 0
            );
        }
        return [...dataSource].sort((a, b) => {
            const aVal = a[orderBy];
            const bVal = b[orderBy];
            if (aVal < bVal) return order === 'asc' ? -1 : 1;
            if (aVal > bVal) return order === 'asc' ? 1 : -1;
            return 0;
        });
    })();

    useEffect(() => {
        if (subItemHeaders) {
            setExpandedRows(
                new Set(dataSource.map((row, index) => row.id ?? `${row.accountName ?? 'account'}-${index}`))
            );
        }
    }, []);

    const toggleRow = (key: string | number) => {
        setExpandedRows((prev) => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    };

    const hasExpandableRows = !!subItemHeaders;
    const colSpan = headers.length + (hasExpandableRows && showExpandButton ? 1 : 0);
    // Index from which trailing columns collapse into a single merged panel (spanning all body rows).
    const mergedFromIndex = trailingMergedCell
        ? headers.findIndex((h) => h.key === trailingMergedCell.fromColumnKey)
        : -1;
    // Columns covered by a rowSpan>1 group-header cell are skipped in the second (main) header row.
    const coveredLeadingCols = new Set<number>();
    if (groupHeaders) {
        let offset = 0;
        groupHeaders.forEach((group) => {
            if ((group.rowSpan ?? 1) > 1) {
                for (let col = offset; col < offset + group.colSpan; col++) coveredLeadingCols.add(col);
            }
            offset += group.colSpan;
        });
    }
    const verticalSticky = !!maxHeight;
    const secondHeaderRowTop = groupHeaders
        ? groupHeaderHeight != null
            ? `${groupHeaderHeight}px`
            : rowHeight || '48px'
        : 0;
    const headerCellStickySx = (
        isFirstColSticky: boolean,
        topOffset: number | string
    ): Record<string, string | number> =>
        !verticalSticky && !isFirstColSticky
            ? {}
            : {
                  position: 'sticky',
                  backgroundColor: theme.palette?.semantic?.bg?.raised,
                  ...(verticalSticky ? { top: topOffset } : {}),
                  ...(isFirstColSticky ? { left: 0 } : {}),
                  zIndex: isFirstColSticky && verticalSticky ? 4 : isFirstColSticky ? 3 : 2
              };
    const stickyBodyCellSx = {
        position: 'sticky',
        left: 0,
        zIndex: 1,
        backgroundColor: theme.palette.common.white
    } as const;
    const isFtds = variant === 'ftds';
    const showGrid = bordered || isFtds;
    const gridBorderColor = borderColor ?? (isFtds ? theme.palette.neutral[200] : theme.palette.divider);
    const noWrap = autoWidth || horizontalScroll;
    // Grid borders on every cell, but drop the outermost left/right verticals to match the design.
    const getCellBorderSx = (isFirstCol: boolean, isLastCol: boolean) =>
        showGrid
            ? {
                  border: `1px solid ${gridBorderColor}`,
                  ...(isFirstCol ? { borderLeft: 'none' } : {}),
                  ...(isLastCol ? { borderRight: 'none' } : {})
              }
            : {};
    const getHeaderBorderSx = (isFirstCol: boolean, isLastCol: boolean) =>
        isFtds
            ? {
                  borderTop: 'none',
                  borderBottom: 'none',
                  borderLeft: isFirstCol ? 'none' : `1px solid ${gridBorderColor}`,
                  borderRight: isLastCol ? 'none' : `1px solid ${gridBorderColor}`
              }
            : getCellBorderSx(isFirstCol, isLastCol);
    return (
        <Box>
            <TableContainer
                sx={{
                    ...(maxHeight ? { maxHeight, overflowY: 'auto' } : {}),
                    ...(horizontalScroll ? { overflowX: 'auto' } : {})
                }}
            >
                <StyledTable
                    size="small"
                    theme={theme}
                    advisorConsoleRevampFF={showExpandButton}
                    ftds={isFtds}
                    embedded={embedded}
                    sx={
                        autoWidth
                            ? { tableLayout: 'auto', width: 'max-content', minWidth: '100%' }
                            : horizontalScroll
                              ? { width: 'max-content', minWidth: '100%' }
                              : undefined
                    }
                >
                    <TableHead>
                        {groupHeaders && (
                            <StyledTableHeaderRow ref={groupHeaderRowRef} theme={theme} $rowHeight={rowHeight}>
                                {hasExpandableRows && showExpandButton && <TableCell sx={{ width: '48px' }} />}
                                {groupHeaders.map((group, groupIndex) => (
                                    <TableCell
                                        key={`group-${groupIndex}`}
                                        align={group.align ?? 'center'}
                                        colSpan={group.colSpan}
                                        rowSpan={group.rowSpan}
                                        sx={{
                                            verticalAlign: 'middle',
                                            ...(group.width ? { width: group.width } : {}),
                                            ...getHeaderBorderSx(
                                                groupIndex === 0,
                                                groupIndex === (groupHeaders?.length ?? 0) - 1
                                            ),
                                            ...(noWrap ? { whiteSpace: 'nowrap' } : {}),
                                            ...headerCellStickySx(stickyFirstColumn && groupIndex === 0, 0),
                                            ...(group.sx ?? {})
                                        }}
                                    >
                                        {group.label}
                                    </TableCell>
                                ))}
                            </StyledTableHeaderRow>
                        )}
                        <StyledTableHeaderRow theme={theme} $rowHeight={rowHeight}>
                            {hasExpandableRows && showExpandButton && <TableCell sx={{ width: '48px' }} />}
                            {headers.map((header, headerIndex) => {
                                if (coveredLeadingCols.has(headerIndex)) return null;
                                const labelContent = header.headerRender ? header.headerRender() : header.sortable ? (
                                    <TableSortLabel
                                        active={orderBy === header.key}
                                        direction={orderBy === header.key ? order : 'asc'}
                                        onClick={() => handleSort(header)}
                                    >
                                        {header.label}
                                    </TableSortLabel>
                                ) : (
                                    header.label
                                );
                                return (
                                    <TableCell
                                        key={header.key}
                                        align={header.align}
                                        sx={{
                                            width: header.width,
                                            verticalAlign: 'middle',
                                            ...getHeaderBorderSx(headerIndex === 0, headerIndex === headers.length - 1),
                                            ...(noWrap ? { whiteSpace: 'nowrap' } : {}),
                                            ...headerCellStickySx(
                                                stickyFirstColumn && headerIndex === 0,
                                                secondHeaderRowTop
                                            ),
                                            ...(header.headerSx ?? {})
                                        }}
                                        sortDirection={header.sortable && orderBy === header.key ? order : false}
                                    >
                                        {header.labelInfo ? (
                                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                                {labelContent}
                                                {header.labelInfo}
                                            </Box>
                                        ) : (
                                            labelContent
                                        )}
                                    </TableCell>
                                );
                            })}
                        </StyledTableHeaderRow>
                    </TableHead>
                    <TableBody>
                        {loading && (
                            <StyledTableRow>
                                <TableCell
                                    colSpan={colSpan}
                                    align="center"
                                    sx={{ verticalAlign: 'middle', padding: '16px 0' }}
                                >
                                    <div style={{ position: 'relative', minHeight: '350px' }}>
                                        <Spinner enabled={true} position="absolute" />
                                    </div>
                                </TableCell>
                            </StyledTableRow>
                        )}
                        {!loading && sortedData.length === 0 && (
                            <StyledTableRow>
                                <TableCell
                                    colSpan={colSpan}
                                    align="center"
                                    sx={{ verticalAlign: 'middle', padding: '16px 0' }}
                                >
                                    <NoDataContainer
                                        sx={isFtds ? { color: theme.palette.semantic.text.muted } : undefined}
                                    >
                                        {t('TEXT_NO_DATA_FOUND')}
                                    </NoDataContainer>
                                </TableCell>
                            </StyledTableRow>
                        )}
                        {!loading &&
                            sortedData.map((row, index) => {
                                const rowKey = row.id ?? `${row.accountName ?? 'account'}-${index}`;
                                const subItems: any[] = row.subItems ?? [];
                                const isExpandable = hasExpandableRows && subItems.length > 0;
                                const isExpanded = expandedRows.has(rowKey);
                                return (
                                    <React.Fragment key={rowKey}>
                                        {customRowComp ? (
                                            customRowComp(headers, row, toggleRow, isExpanded, rowKey)
                                        ) : (
                                            <StyledTableRow
                                                sx={conditionalRowFormat ? conditionalRowFormat(row) : {}}
                                                $rowHeight={bodyRowHeight ?? rowHeight}
                                            >
                                                {hasExpandableRows && showExpandButton && (
                                                    <TableCell
                                                        sx={{
                                                            width: '48px',
                                                            verticalAlign: 'middle',
                                                            padding: '8px 8px'
                                                        }}
                                                    >
                                                        {isExpandable && (
                                                            <ExpandButton
                                                                isExpanded={isExpanded}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    toggleRow(rowKey);
                                                                }}
                                                            >
                                                                <Icon id="expander" size={21} />
                                                            </ExpandButton>
                                                        )}
                                                    </TableCell>
                                                )}
                                                {headers.map((header, headerIndex) => {
                                                    if (mergedFromIndex >= 0 && headerIndex > mergedFromIndex)
                                                        return null;
                                                    if (mergedFromIndex >= 0 && headerIndex === mergedFromIndex) {
                                                        if (index !== 0) return null;
                                                        return (
                                                            <TableCell
                                                                key="__trailing_merged__"
                                                                align="center"
                                                                colSpan={headers.length - mergedFromIndex}
                                                                rowSpan={sortedData.length}
                                                                sx={{
                                                                    verticalAlign: 'middle',
                                                                    fontSize: '16px',
                                                                    fontWeight: isFtds ? 400 : 500,
                                                                    lineHeight: isFtds ? '22px' : '20px',
                                                                    ...(isFtds ? { color: theme.palette.neutral[950] } : {}),
                                                                    ...getCellBorderSx(false, true),
                                                                    ...(trailingMergedCell?.sx ?? {})
                                                                }}
                                                            >
                                                                {trailingMergedCell?.content}
                                                            </TableCell>
                                                        );
                                                    }
                                                    return (
                                                        <TableCell
                                                            key={header.key}
                                                            align={header.align}
                                                            sx={{
                                                                width: header.width,
                                                                verticalAlign: 'middle',
                                                                fontSize: '16px',
                                                                fontWeight: isFtds ? 400 : 500,
                                                                lineHeight: isFtds ? '22px' : '20px',
                                                                ...(isFtds ? { color: theme.palette.neutral[950] } : {}),
                                                                ...getCellBorderSx(
                                                                    headerIndex === 0,
                                                                    headerIndex === headers.length - 1
                                                                ),
                                                                ...(noWrap ? { whiteSpace: 'nowrap' } : {}),
                                                                ...(stickyFirstColumn && headerIndex === 0
                                                                    ? stickyBodyCellSx
                                                                    : {}),
                                                                ...(header.cellSx ?? {})
                                                            }}
                                                        >
                                                            {(header.render
                                                                ? header.render(row[header.key], row)
                                                                : row?.[header.key] ||
                                                                  (header.key == 'dummy' ? ' ' : '-')) || '-'}
                                                        </TableCell>
                                                    );
                                                })}
                                            </StyledTableRow>
                                        )}
                                        {isExpandable && (
                                            <TableRow>
                                                <TableCell colSpan={colSpan} sx={{ padding: 0, borderBottom: 'none' }}>
                                                    <Collapse in={isExpanded} sx={{ transition: 'none' }} unmountOnExit>
                                                        <Table
                                                            size="small"
                                                            sx={{ tableLayout: 'fixed', width: '100%' }}
                                                        >
                                                            <TableBody>
                                                                {subItems.map((subItem, subIndex) =>
                                                                    customSubRowComp ? (
                                                                        customSubRowComp(subItemHeaders, subItem)
                                                                    ) : (
                                                                        <TableRow key={subItem.id ?? subIndex}>
                                                                            {hasExpandableRows && showExpandButton && (
                                                                                <TableCell sx={{ width: '48px' }} />
                                                                            )}
                                                                            {subItemHeaders!.map((col) => (
                                                                                <TableCell
                                                                                    key={col.key}
                                                                                    align={col.align}
                                                                                    sx={{
                                                                                        width: col.width,
                                                                                        verticalAlign: 'middle',
                                                                                        fontSize: '16px',
                                                                                        fontWeight: isFtds ? 400 : 500,
                                                                                        lineHeight: isFtds ? '22px' : '20px',
                                                                                        ...(isFtds
                                                                                            ? { color: theme.palette.neutral[950] }
                                                                                            : {})
                                                                                    }}
                                                                                >
                                                                                    {(col.render
                                                                                        ? col.render(
                                                                                              subItem[col.key],
                                                                                              subItem
                                                                                          )
                                                                                        : subItem?.[col.key] ||
                                                                                          (col.key == 'dummy'
                                                                                              ? ' '
                                                                                              : '-')) || '-'}
                                                                                </TableCell>
                                                                            ))}
                                                                        </TableRow>
                                                                    )
                                                                )}
                                                            </TableBody>
                                                        </Table>
                                                    </Collapse>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                    </TableBody>
                </StyledTable>
            </TableContainer>
        </Box>
    );
};
